import { DetailViewContentElementType } from "~/enums/detailViews/detailViewContentElementType";
import { IDetailView } from '~/interfaces/detailsViewInterfaces';
import { IOrganizationDocument } from "~/interfaces/organizationDocumentInterfaces";
import { IPeekerOptions } from "~/services/peeker";
import Action from "../../../formability/detailViewBuilder/models/action";
import DocumentLink from "../../../formability/detailViewBuilder/models/documentLink";
import FileProperty from "../../../formability/detailViewBuilder/models/fileProperty";
import FormList from "../../../formability/detailViewBuilder/models/formList";
import Heading from "../../../formability/detailViewBuilder/models/heading";
import ModelHistory from "../../../formability/detailViewBuilder/models/modelHistory";
import ModelProperty from "../../../formability/detailViewBuilder/models/modelProperty";
import PageHeader from "../../../formability/detailViewBuilder/models/pageHeader";
import { RelatedObject } from "../../../formability/detailViewBuilder/models/relatedObject";
import Subheading from "../../../formability/detailViewBuilder/models/subheading";
import { TabBreak } from "../../../formability/detailViewBuilder/models/tabBreak";
import Selected from "../../../formability/detailViewBuilder/store/modules/selected";
import Folder from "../../../formability/document_management/models/folder";
import TrackedArrayProxy from "../../../formability/form_builder/models/trackedArrayProxy";
import IRootBuilderModel from "../../interfaces/builderModelInterfaces";
import IRepo from "../../interfaces/repo";
import Documentable from "../../mixins/documentable";
import Validatable from "../../mixins/validatable";
import DetailViewProvider from "../../providers/detailViewProvider";
import ElementHandler from "../../services/elementHandler";
import Repo from "../../state/modules/repo";
import store from "../../state/store";
import Grid from "../contentManagement/commonElements/grid";
import ContentElementable from "../contentManagement/contentElementable";
import PublishedDetailViewDecorator from '../decorators/publishedDetailViewDecorator';
import DetailViewBuilderValidation from "../navigableValidations/detailViewBuilderValidation";
import ObjectModel from "../objectModel";
import PublishedObjectModel from "../objectModels/publishedObjectModel";
import Organization from "../organization";
import { RelationType } from "../relationType";
import TrackedModel from "../tracked/trackedModel";
import DetailViewContentElement from "./elements/detailViewContentElement";
import {RelationSection} from "../../../formability/detailViewBuilder/models/relationSection";
import Share from "~/models/share";
import Shareable from "~/mixins/shareable";
import {DocumentType} from "~/enums/documentType";

export default class DetailView extends Validatable(ContentElementable(Documentable(Shareable(TrackedModel)))) implements IRootBuilderModel, IOrganizationDocument, IDetailView {
  _classKey = "detailView";
  detailView: this;
  displayName: string;
  _organization: Organization;
  attempted_to_publish?: boolean;
  author: any;
  folder_id: string;
  title: string;
  tabs: string[] = [''];
  _contentElementHandler = new ElementHandler(this, DetailView, 'content_elements');
  object_model: ObjectModel;
  published_object_model?: PublishedObjectModel;
  current_tab_index: number = 0;
  _publishedRecord: PublishedDetailViewDecorator
  permitted_related_holder_models: string[][];
  current_embedded_list_view_ce_id: string;
  object_model_id: string;

  constructor() {
    super();
    const proxyTabs = Object.assign([''], {parent: this, key: 'tabs'});
    this.defineProperty('tabs', TrackedArrayProxy.create(proxyTabs));
    const proxyModels = Object.assign([{}], {parent: this, key: 'permitted_related_holder_models'});
    this.defineProperty('permitted_related_holder_models', TrackedArrayProxy.create(proxyModels));
  }

  get objectModel(){
    return this.object_model
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      content_elements: {type: DetailViewContentElement, relationType: RelationType.dictionary},
      object_model: {type: ObjectModel, relationType: RelationType.singleObject},
      shares: {type: Share, relationType: RelationType.collection},
    };
  }

  createEmbeddedObject = {
    content_elements: (parent, json) => this.detailViewContentElementFactory(json["content_type"]),
    object_model: (parent, json): ObjectModel => new ObjectModel(),
  };

  get TRACKED_ARRAYS(): string[] {
    return [
      'tabs',
      'permitted_related_holder_models',
    ];
  }

  set organization(val: Organization) {
    this._organization = val;
  }

  get organization(): Organization {
    if (this._organization) {
      // for TrackedModel implementation
      return this._organization;
    } else if (this.parent instanceof Organization) {
      // for OrganizationDocument implementation
      return this.parent;
    } else {
      return this.parent.organization;
    }
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Detail View";
    }
  }

  except = [
    'detailView',
    'createEmbeddedObject',
    'organization',
    'saveTimer',
    '_organization',
    'object_model',
    'shares',
  ];

  static repo: IRepo<DetailView, DetailViewProvider> = new Repo<DetailView, DetailViewProvider>('detailViews', DetailView, {}, new DetailViewProvider(DetailView, 'detail_view', 'detail_views'));
  repo: IRepo<this, DetailViewProvider<this>>;

  static get provider(): DetailViewProvider {
    return this.repo.provider;
  }

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  static get displayName(): string {
    return 'Detail View';
  }

  get collectionKey() {
    return "detail_views";
  }

  get classKey(): string {
    return "detailView";
  }

  get docType(): DocumentType {
    return DocumentType.detailView;
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      'u_at',
      'object_model'
    ];
  }


  get rootName() {
    return 'detailView';
  }

  get selectedContentElement(): DetailViewContentElement {
    return Selected.detailViewContentElement.get();
  }

  detailViewContentElementFactory(elementType): DetailViewContentElement {
    let el: DetailViewContentElement;
    switch (elementType) {
      case DetailViewContentElementType.TabBreak:
        el = new TabBreak();
        break;
      case DetailViewContentElementType.ModelProperty:
        el = new ModelProperty();
        break;
      case DetailViewContentElementType.RelatedObject:
        el = new RelatedObject();
        break;
      case DetailViewContentElementType.Heading:
        el = new Heading();
        break;
      case DetailViewContentElementType.Subheading:
        el = new Subheading();
        break;
      case DetailViewContentElementType.Grid:
        el = new Grid();
        break;
      case DetailViewContentElementType.Header:
        el = new PageHeader();
        break;
      case DetailViewContentElementType.Action:
        el = new Action();
        break;
      case DetailViewContentElementType.DocumentLink:
        el = new DocumentLink();
        break;
      case DetailViewContentElementType.ModelHistory:
        el = new ModelHistory();
        break;
      case DetailViewContentElementType.FormList:
        el = new FormList();
        break;
      case DetailViewContentElementType.FileProperty:
        el = new FileProperty();
        break;
      case DetailViewContentElementType.RelationSection:
        el = new RelationSection();
        break;
      default:
        el = new DetailViewContentElement();
    }
    return el;
  }

  // ******************** VALIDATABLE******************** //
  _validation: DetailViewBuilderValidation = new DetailViewBuilderValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  // ******************** VALIDATABLE END******************** //

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(this.lastPeekerOptions || {
        area: this.currentBuilderArea(),
        modelId: this.clientIdOrId
      });
      if (this.attempted_to_publish) {
        this.validate();
      }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.storeName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      this.registerAutosaveError(err);
      let message = `Error: ${err.status} - ${err.statusText}`;
      if (window.environment === "test") {
        message += ` - ${err.responseText}`
      }
      store.commit('flashStore/set', {
        flash: {
          alert: message
        }
      });
    });
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = {
    area: this.currentBuilderArea(),
    modelId: this.clientIdOrId
  }): void {
    store.commit(`${this.storeName}/saveChanges`, {changes, peekerOptions});
  }

  // ******************** TRACK CHANGES END ******************** //

  get isPublished(): boolean {
    return this.is_published && Date.now() > Date.parse(this.u_at);
  }

  get isArchive(): boolean {
    return this.parent instanceof Folder && (this.parent.name === 'Archive' || this.parent.name === 'Trash');
  }

  canMoveTo(folder: Folder): boolean {
    return !folder.isArchiveFolder();
  }

  // ******************** TAB BREAK ******************** //

  get tabBreaks(): TabBreak[] {
    const tabBreakArray = <TabBreak[]>this.contentElementsArray.filter(fe => fe.content_type === DetailViewContentElementType.TabBreak);
    return tabBreakArray.sort((a, b) => a.sort_order - b.sort_order);
  }

  setCurrentTabFormURL(tabName) {
    //Checks if tabName matches placeholder if it does string the word tab in order to compare tab number with index
    let name =  tabName;
    let index;
    this.tabs.forEach((x, i) => {
      //Adds one to compensate for 0 index, and removed spaces from tabName
      if (x.replace(/" "/g, "").toLowerCase() === name || i + 1 === parseInt(name, 10)) {
        //subtracts one to compensate for current tab index starting at -1
        index = i;
      }
    });
    this.current_tab_index = index;
  }

  get contentElementsIndicesInCurrentTab(): number[] {
    let currentTabBreak;
    //The first tab is a special case
    if (this.current_tab_index === 0) {
      currentTabBreak = this.tabBreaks[0];
      return [currentTabBreak.priorTabContentElementsStartIndex, currentTabBreak.priorTabContentElementsEndIndex]
    } else {
      currentTabBreak = this.tabBreaks[this.current_tab_index-1];
      return [currentTabBreak.nextTabContentElementsStartIndex, currentTabBreak.nextTabContentElementsEndIndex]
    }
  }

  buildTab(name: string = ''): void {
    this.tabs.push(name);
  }

  destroyTab(pageIndex: number = this.tabs.length - 1): void {
    this.tabs.splice(pageIndex, 1);
  }

  async fetchPublishedRecord(): Promise<any> {
    // TODO: Use this similar to object models
  }

  buildPublishedRecord(json: object = {}): this['_publishedRecord'] {
    this._publishedRecord = new PublishedDetailViewDecorator(json)
    return this.publishedRecord
  }

  get publishedRecord(): this['_publishedRecord'] {
    return this._publishedRecord
  }

  _moving: boolean = false
  get moving(): boolean {
    return this._moving
  }

  set moving(val: boolean) {
    this._moving = val
  }

  get embeddedListViewTitle(): string {
    return this.content_elements[this.current_embedded_list_view_ce_id]?.label_title
  }

  get headerContentElement() {
    return this.contentElementsArray.find(fe => fe.content_type === DetailViewContentElementType.Header)
  }
}
